import React, { useState } from 'react';
import OlcButton from '~/components/General/OlcButton';
import OlcTooltip from '~/components/General/OlcTooltip';

// Hooks
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { failure } from '~/redux/actions/snackbar-actions';
import { downgradePlanCost, upgradePlanCost } from '~/redux/actions/stripe-actions';

// Utils
import { MESSAGES } from '~/utils/message';

// MUI Components
import { Box, Typography, CircularProgress } from '@mui/material';

// Icons
import LayersOutlinedIcon from '@mui/icons-material/LayersOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUpOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';

// styles
import './styles.scss';

const FIND_NEW_CONTACTS_ACCESS_SLUG = 'find_new_contacts_access_allowed';
const FIND_NEW_CONTACTS_COST_SLUG = 'find_new_contacts_cost_per_record';

const isFeatureEnabled = (value) =>
  value === true || value === 1 || value === '1' || value === 'true' || value === 'yes';

const FIND_NEW_CONTACTS_FEATURES = {
  [FIND_NEW_CONTACTS_ACCESS_SLUG]: {
    formatValue: (value) => (isFeatureEnabled(value) ? 'Yes' : 'No'),
    label: 'Find New Contacts Access',
  },
};

const getFeaturePresentation = (feature) => FIND_NEW_CONTACTS_FEATURES[feature.slug];

const orderFeatures = (features) => {
  const accessFeature = features.find(({ slug }) => slug === FIND_NEW_CONTACTS_ACCESS_SLUG);
  const otherFeatures = features.filter(
    ({ slug }) => slug !== FIND_NEW_CONTACTS_ACCESS_SLUG && slug !== FIND_NEW_CONTACTS_COST_SLUG
  );

  return [...otherFeatures, ...(accessFeature ? [accessFeature] : [])];
};

const formatFeatureValue = (feature, presentation) => {
  if (presentation?.formatValue) {
    return presentation.formatValue(feature.value);
  }

  if (feature.value === true) return 'Yes';
  if (feature.value === false) return 'No';

  return feature.value;
};

const index = (props) => {
  const {
    title,
    features,
    featuresV2,
    plan,
    billAnnual,
    annual,
    isActive,
    currentPlanStripId,
    currentPlanType,
    stripeId,
    handleOpen,
    handleDowngradeOpen,
    plansOrder,
    currentPlan,
    selectedType,
    selectedPlanType,
    setPlanDetails,
    previewMode,
  } = props;
  const [isViewMore, setIsViewMore] = useState(false);

  const planIcons = {
    starter: LayersOutlinedIcon,
    stater: LayersOutlinedIcon,
    growth: TrendingUpIcon,
    professional: PersonOutlineIcon,
    enterprise: BusinessOutlinedIcon,
  };
  const PlanIcon = planIcons[selectedType?.toLowerCase()] || LayersOutlinedIcon;

  const {
    users,
    webhooks,
    userRolesPermissions,
    templates,
    scheduleMailing,
    mailingLimit,
    mailTracking,
    contacts,
    ayalyticsDashboard,
    customFields,
    tags,
    zapier,
    goHighLevel,
    hubspot,
    shopify,
    sdk,
  } = features;

  const orderArr = ['starter', 'growth', 'professional', 'enterprise'];
  const currentIndex = orderArr.indexOf(currentPlan?.slug);
  const selectedIndex = orderArr.indexOf(selectedType);
  const [isLoading, setIsLoading] = useState(false);

  const isAdmin = useSelector((state) => state.userReducers?.user?.isOrgAdmin) || false;

  const isSystemAdmin = useSelector((state) => state?.userReducers?.isSystemAdmin) || false;

  const planActionDisabled = !isAdmin && !previewMode;

  const runPlanRequest = (request) => {
    if (previewMode || !isAdmin) return;
    request(stripeId);
  };

  const dispatch = useDispatch();

  const stripeIdies = plansOrder.map((plan) => plan.stripeId);

  const findIndex = (stripeId) => {
    return stripeIdies.findIndex((id) => id === stripeId);
  };

  const downgradeRequest = async (stripeId) => {
    try {
      setIsLoading(true);
      const response = await downgradePlanCost({
        planId: stripeId,
      });
      if (response.status === 200) {
        setPlanDetails(response.data.data);
        handleDowngradeOpen(stripeId);
      } else {
        dispatch(failure(response?.data?.message));
      }
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message));
    } finally {
      setIsLoading(false);
    }
  };

  const upgradeRequest = async (stripeId) => {
    try {
      setIsLoading(true);
      const response = await upgradePlanCost({ planId: stripeId });
      if (response.status === 200) {
        setPlanDetails(response.data.data);
        handleOpen(stripeId);
      } else {
        dispatch(failure(response?.data?.message));
      }
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      className={`${
        isActive ? `cardWrapperActive cardWrapper` : `cardWrapper`
      } ${isViewMore ? `heightIncrease` : 'heightDecrease'}`}
    >
      <Box className="cardHead">
        <PlanIcon className="planCardHeaderIcon" />
        <Typography variant="h6">{title}</Typography>
      </Box>
      <Box className="featuresWrapper">
        {orderFeatures(featuresV2 || []).map((feat) => {
          const presentation = getFeaturePresentation(feat);

          return (
            (feat.show || presentation) && (
              <Box className="feature" key={feat.slug}>
                <Typography>{presentation?.label || feat?.label || feat?.slug || ''}</Typography>
                <Typography>{formatFeatureValue(feat, presentation)}</Typography>
              </Box>
            )
          );
        })}
        {!featuresV2?.length && (
          <>
            {users.show && (
              <Box className="feature">
                <Typography>{MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.LABELS.USERS}</Typography>
                <Typography>
                  {users.limit === -1 || isSystemAdmin ? 'Unlimited' : users.limit}
                </Typography>
              </Box>
            )}
            {contacts.show && (
              <Box className="feature">
                <Typography>{MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.LABELS.CONTACTS}</Typography>
                <Typography>
                  {contacts.limit === -1 || isSystemAdmin ? 'Unlimited' : `Up to ${contacts.limit}`}
                </Typography>
              </Box>
            )}
            {templates.show && (
              <Box className="feature">
                <Typography>{MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.LABELS.TEMPLATES}</Typography>
                <Typography>
                  {templates.limit === -1 || isSystemAdmin ? 'Unlimited' : templates.limit}
                </Typography>
              </Box>
            )}
            {customFields.show && (
              <Box className="feature">
                <Typography>{MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.LABELS.CUSTOM_FIELDS}</Typography>
                <Typography>
                  {customFields.limit === -1 || isSystemAdmin ? 'Unlimited' : customFields.limit}
                </Typography>
              </Box>
            )}
            {webhooks.show && (
              <Box className="feature">
                <Typography>{MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.LABELS.WEBHOOKS}</Typography>
                <Typography>
                  {webhooks.limit === -1 || isSystemAdmin ? 'Unlimited' : webhooks.limit}
                </Typography>
              </Box>
            )}
            {tags?.show && (
              <Box className="feature">
                <Typography>{MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.LABELS.TAGS}</Typography>
                <Typography>
                  {tags.limit === -1 || isSystemAdmin ? 'Unlimited' : tags.limit}
                </Typography>
              </Box>
            )}
            {isViewMore && (
              <>
                {mailingLimit.show && (
                  <Box className="feature">
                    <Typography>{MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.LABELS.MAILING}</Typography>
                    <Typography>
                      {mailingLimit.limit === -1 || isSystemAdmin
                        ? 'Unlimited'
                        : `Up to ${mailingLimit.limit}`}
                    </Typography>
                  </Box>
                )}
                {mailTracking.show && (
                  <Box className="feature">
                    <Typography>{MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.LABELS.TRANSIT}</Typography>
                    <Typography>{mailTracking.limit || isSystemAdmin ? 'Yes' : 'No'} </Typography>
                  </Box>
                )}
                {userRolesPermissions.show && (
                  <Box className="feature">
                    <Typography>
                      {MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.LABELS.ROLES_PERMISSION}
                    </Typography>
                    <Typography>
                      {userRolesPermissions.limit || isSystemAdmin ? 'Yes' : 'No'}{' '}
                    </Typography>
                  </Box>
                )}
                {ayalyticsDashboard.show && (
                  <Box className="feature">
                    <Typography>{MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.LABELS.ANALYTICS}</Typography>
                    <Typography>
                      {ayalyticsDashboard.limit || isSystemAdmin ? 'Advanced' : 'Basic'}{' '}
                    </Typography>
                  </Box>
                )}
                {scheduleMailing.show && (
                  <Box className="feature">
                    <Typography>{MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.LABELS.MAILER}</Typography>
                    <Typography>
                      {scheduleMailing.limit || isSystemAdmin ? 'Yes' : 'No'}{' '}
                    </Typography>
                  </Box>
                )}
                {zapier.show && (
                  <Box className="feature">
                    <Typography>{MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.LABELS.ZAPIER}</Typography>
                    <Typography>{zapier.limit ? 'Yes' : 'No'} </Typography>
                  </Box>
                )}
                {goHighLevel.show && (
                  <Box className="feature">
                    <Typography>
                      {MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.LABELS.GO_HIGH_LEVEL}
                    </Typography>
                    <Typography>{goHighLevel.limit ? 'Yes' : 'No'} </Typography>
                  </Box>
                )}
                {hubspot.show && (
                  <Box className="feature">
                    <Typography>{MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.LABELS.HUBSPOT}</Typography>
                    <Typography>{hubspot.limit ? 'Yes' : 'No'} </Typography>
                  </Box>
                )}
                {shopify.show && (
                  <Box className="feature">
                    <Typography>{MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.LABELS.SHOPIFY}</Typography>
                    <Typography>{shopify.limit ? 'Yes' : 'No'} </Typography>
                  </Box>
                )}
                {sdk.show && (
                  <Box className="feature">
                    <Typography>{MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.LABELS.SDK}</Typography>
                    <Typography>{sdk.limit ? 'Yes' : 'No'} </Typography>
                  </Box>
                )}
              </>
            )}
          </>
        )}

        <Box className="details">
          {/* <Typography onClick={() => setIsViewMore((prev) => !prev)} className="view-more-less">
            {isViewMore ? 'View Less' : 'View more details'}
          </Typography> */}
          <Typography>
            {currentPlan?.slug === 'enterprise' && currentPlan?.name === title ? '' : plan}
          </Typography>
          {plan === 'Free' ? (
            ''
          ) : (
            <Typography className="billAnnual">
              {annual && billAnnual.length > 0 ? `(${billAnnual})` : ''}
            </Typography>
          )}
          {!annual ? (
            <Typography className="billAnnual">
              {billAnnual.length > 0 ? `(${billAnnual})` : ''}
            </Typography>
          ) : (
            ''
          )}
        </Box>
      </Box>
      {currentPlanStripId == stripeId ? (
        <OlcButton variant="secondary" className="currentPlanButton" disabled>
          {MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.CURRENT_PLAN_BTN}
        </OlcButton>
      ) : (
        <>
          {plan !== 'Talk to Sales' &&
          currentIndex === selectedIndex &&
          currentPlanType !== selectedPlanType ? (
            <>
              {currentPlanType === 'year' ? (
                <OlcTooltip
                  title={
                    previewMode
                      ? 'Preview only — plan changes are disabled locally.'
                      : !isAdmin
                        ? 'Only the Admins on your account are authorized to downgrade the plan.'
                        : ''
                  }
                >
                  <OlcButton
                    variant="primary"
                    onClick={() => runPlanRequest(downgradeRequest)}
                    className={`planActionButton${planActionDisabled ? ' disabled' : ''}`}
                    disabled={isLoading}
                    aria-busy={isLoading || undefined}
                  >
                    {isLoading ? (
                      <CircularProgress
                        sx={{
                          color: 'white',
                          width: '25px !important',
                          height: '25px !important',
                        }}
                      />
                    ) : (
                      MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.DOWNGRADE_PLAN_BTN
                    )}
                  </OlcButton>
                </OlcTooltip>
              ) : (
                <OlcTooltip
                  title={
                    previewMode
                      ? 'Preview only — plan changes are disabled locally.'
                      : !isAdmin
                        ? 'Only the Admins on your account are authorized to upgrade the plan.'
                        : ''
                  }
                >
                  <OlcButton
                    variant="primary"
                    onClick={() => runPlanRequest(upgradeRequest)}
                    className={`planActionButton${planActionDisabled ? ' disabled' : ''}`}
                    disabled={isLoading}
                    aria-busy={isLoading || undefined}
                  >
                    {isLoading ? (
                      <CircularProgress
                        sx={{
                          color: 'white',
                          width: '25px !important',
                          height: '25px !important',
                        }}
                      />
                    ) : (
                      MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.UPGRADE_PLAN_BTN
                    )}
                  </OlcButton>
                </OlcTooltip>
              )}
            </>
          ) : plan !== 'Talk to Sales' && currentIndex <= selectedIndex ? (
            <OlcTooltip
              title={
                previewMode
                  ? 'Preview only — plan changes are disabled locally.'
                  : !isAdmin
                    ? 'Only the Admins on your account are authorized to upgrade the plan.'
                    : ''
              }
            >
              <OlcButton
                variant="primary"
                onClick={() => runPlanRequest(upgradeRequest)}
                className={`planActionButton${planActionDisabled ? ' disabled' : ''}`}
                disabled={isLoading}
                aria-busy={isLoading || undefined}
              >
                {isLoading ? (
                  <CircularProgress
                    sx={{
                      color: 'white',
                      width: '25px !important',
                      height: '25px !important',
                    }}
                  />
                ) : (
                  MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.UPGRADE_PLAN_BTN
                )}
              </OlcButton>
            </OlcTooltip>
          ) : (
            plan !== 'Talk to Sales' &&
            currentIndex >= selectedIndex && (
              <OlcTooltip
                title={
                  previewMode
                    ? 'Preview only — plan changes are disabled locally.'
                    : !isAdmin
                      ? 'Only the Admins on your account are authorized to downgrade the plan.'
                      : ''
                }
              >
                <OlcButton
                  variant="primary"
                  onClick={() => runPlanRequest(downgradeRequest)}
                  className={`planActionButton${planActionDisabled ? ' disabled' : ''}`}
                  disabled={isLoading}
                  aria-busy={isLoading || undefined}
                >
                  {isLoading ? (
                    <CircularProgress
                      sx={{
                        color: 'white',
                        width: '25px !important',
                        height: '25px !important',
                      }}
                    />
                  ) : (
                    MESSAGES.ACCOUNT.PLANS.PLAN_CARDS.DOWNGRADE_PLAN_BTN
                  )}
                </OlcButton>
              </OlcTooltip>
            )
          )}
        </>
      )}
    </Box>
  );
};

export default index;
